import AbstractAsyncDetailTablePage from '../abstract-async-detail-table-page';
import InvoiceSearchForm from '../../components/orders/invoice-search-form';
import SearchForm from '../../model/search-form';
import Table from '../../components/shared/table';
import InvoiceData from '../../model/invoice-data';
import {
  formatCurrency, formatDate, formatNumber, formatTime,
} from '../../helpers/utils';
import MessageHelper from '../../helpers/message-helper';
import HTMLTemplateHelper from '../../helpers/html-template-helper';
import InvoiceEntryData from '../../model/invoice-entry-data';

export default class InvoicePage extends AbstractAsyncDetailTablePage<InvoiceData> {
  protected readonly searchForm: SearchForm;
  protected readonly table: Table;
  protected readonly tableColumnDefinitions: DataTables.ColumnDefsSettings[] = [
    {
      className: 'js-showDetails',
      targets: 0,
      orderable: false,
      searchable: false,
      data: null,
      defaultContent: '<i class="table__toggle-child fas fa-chevron-right"></i>',
    }, {
      targets: 1,
      data: (data: InvoiceData): string => `<a>${data.id}</a>`,
      className: 'cursor-pointer js-showDetails',
    },
    { targets: 2, data: 'orderId' },
    { targets: 3, data: ({ status }: InvoiceData): string => MessageHelper.getMessage(status) },
    {
      targets: 4,
      data: (data: InvoiceData, type: string): string | number => {
        if (data.submittedDate === null) {
          return 0;
        }
        if (type === 'sort') {
          return data.submittedDate;
        }
        return formatDate(new Date(data.submittedDate));
      },
    },
    {
      targets: 5,
      data: (data: InvoiceData, type: string): string | number => {
        if (data.submittedDate === null) {
          return 0;
        }
        if (type === 'sort') {
          return data.submittedDate;
        }
        return formatTime(new Date(data.submittedDate));
      },
    },
    { targets: 6, data: 'quantity', className: 'text-align-right' },
    { targets: 7, data: ({ netAmount }: InvoiceData): string => formatNumber(netAmount, 2), className: 'text-align-right' },
    { targets: 8, data: ({ grossAmount }: InvoiceData): string => formatNumber(grossAmount, 2), className: 'text-bold text-align-right' },
    {
      targets: 9,
      data: (data: InvoiceData): string => HTMLTemplateHelper.createLinkButton({
        buttonClass: '-clear -compact',
        href: `${window.ACC.config.encodedContextPath}/orders/invoice/print?token=${data.fileDownloadJwt}`,
        text: MessageHelper.getMessage('common.print'),
        textClass: 'sr-only',
        icon: 'fas fa-file-download',
      }).outerHTML,
      className: 'text-align-center',
    },
  ];

  protected readonly detailTableTemplate: JQuery<HTMLTemplateElement>;
  protected readonly detailTableColumnDefinitions: DataTables.ColumnDefsSettings[] = [
    { targets: 0, data: 'id', width: '5%' },
    { targets: 1, data: 'materialNumber', width: '20%' },
    {
      targets: 2, data: 'quantity', className: 'text-align-right', width: '5%',
    },
    { targets: 3, data: 'materialDescription', width: '20%' },
    {
      targets: 4, data: ({ priceWithoutVat }: InvoiceEntryData): string => formatNumber(priceWithoutVat, 2), className: 'text-align-right', width: '10%',
    },
    { targets: 5, data: 'referenceNumber', width: '20%' },
    {
      targets: 6,
      data: ({ deliveryNumber }: InvoiceEntryData): string | null => {
        if (deliveryNumber && deliveryNumber !== '-') {
          return `<a href="/orders/deliverynote?id=${deliveryNumber}">${deliveryNumber}</a>`;
        }
        return null;
      },
      width: '20%',
    },
  ];

  private readonly totalAmount: JQuery;

  constructor(body: JQuery) {
    super();
    this.searchForm = new InvoiceSearchForm(body.find('.js-invoiceForm') as JQuery<HTMLFormElement>);
    this.totalAmount = body.find('.js-tableSumValue');
    this.detailTableTemplate = body.find<HTMLTemplateElement>('#js-invoiceDetailsTemplate');
    this.table = new Table(body.find('.js-table'), this.tableColumnDefinitions, { drawCallback: this.generateDrawCallback('id') });
    this.bindAsyncData();
    this.bindShowDetails();
  }

  protected setRows(data: InvoiceData[]): void {
    super.setRows(data);
    const total = data.reduce((sum, invoice) => sum + invoice.amount ?? 0, 0);
    this.totalAmount.text(formatCurrency(total));
  }

  // eslint-disable-next-line class-methods-use-this
  protected getDetailUrl(invoice: InvoiceData): string {
    return `${window.ACC.config.encodedContextPath}/orders/invoice/details/${invoice.id}`;
  }
}
